"use strict";

Object.defineProperty(exports, "__esModule", {
    value: true
});

var _bluebird = require("bluebird");

var _classCallCheck2 = require("babel-runtime/helpers/classCallCheck");

var _classCallCheck3 = _interopRequireDefault(_classCallCheck2);

var _createClass2 = require("babel-runtime/helpers/createClass");

var _createClass3 = _interopRequireDefault(_createClass2);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/**
 * Base class for classes that provide platform-specific event indexing.
 *
 * Instances of this class are provided by the application.
 */
/*
Copyright 2019 The Matrix.org Foundation C.I.C.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

var BaseEventIndexManager = function () {
    function BaseEventIndexManager() {
        (0, _classCallCheck3.default)(this, BaseEventIndexManager);
    }

    (0, _createClass3.default)(BaseEventIndexManager, [{
        key: "supportsEventIndexing",

        /**
         * Does our EventIndexManager support event indexing.
         *
         * If an EventIndexManager implementor has runtime dependencies that
         * optionally enable event indexing they may override this method to perform
         * the necessary runtime checks here.
         *
         * @return {Promise} A promise that will resolve to true if event indexing
         * is supported, false otherwise.
         */
        value: function () {
            var _ref = (0, _bluebird.method)(function () {
                return true;
            });

            function supportsEventIndexing() {
                return _ref.apply(this, arguments);
            }

            return supportsEventIndexing;
        }()
        /**
         * Initialize the event index for the given user.
         *
         * @return {Promise} A promise that will resolve when the event index is
         * initialized.
         */

    }, {
        key: "initEventIndex",
        value: function () {
            var _ref2 = (0, _bluebird.method)(function () {
                throw new Error("Unimplemented");
            });

            function initEventIndex() {
                return _ref2.apply(this, arguments);
            }

            return initEventIndex;
        }()

        /**
         * Queue up an event to be added to the index.
         *
         * @param {MatrixEvent} ev The event that should be added to the index.
         * @param {MatrixProfile} profile The profile of the event sender at the
         * time of the event receival.
         *
         * @return {Promise} A promise that will resolve when the was queued up for
         * addition.
         */

    }, {
        key: "addEventToIndex",
        value: function () {
            var _ref3 = (0, _bluebird.method)(function (ev, profile) {
                throw new Error("Unimplemented");
            });

            function addEventToIndex(_x, _x2) {
                return _ref3.apply(this, arguments);
            }

            return addEventToIndex;
        }()

        /**
         * Check if our event index is empty.
         */

    }, {
        key: "indexIsEmpty",
        value: function indexIsEmpty() {
            throw new Error("Unimplemented");
        }

        /**
         * Commit the previously queued up events to the index.
         *
         * @return {Promise} A promise that will resolve once the queued up events
         * were added to the index.
         */

    }, {
        key: "commitLiveEvents",
        value: function () {
            var _ref4 = (0, _bluebird.method)(function () {
                throw new Error("Unimplemented");
            });

            function commitLiveEvents() {
                return _ref4.apply(this, arguments);
            }

            return commitLiveEvents;
        }()

        /**
         * Search the event index using the given term for matching events.
         *
         * @param {SearchArgs} searchArgs The search configuration sets what should
         * be searched for and what should be contained in the search result.
         *
         * @return {Promise<[SearchResult]>} A promise that will resolve to an array
         * of search results once the search is done.
         */

    }, {
        key: "searchEventIndex",
        value: function () {
            var _ref5 = (0, _bluebird.method)(function (searchArgs) {
                throw new Error("Unimplemented");
            });

            function searchEventIndex(_x3) {
                return _ref5.apply(this, arguments);
            }

            return searchEventIndex;
        }()

        /**
         * Add events from the room history to the event index.
         *
         * This is used to add a batch of events to the index.
         *
         * @param {[HistoricEvent]} events The list of events and profiles that
         * should be added to the event index.
         * @param {[CrawlerCheckpoint]} checkpoint A new crawler checkpoint that
         * should be stored in the index which should be used to continue crawling
         * the room.
         * @param {[CrawlerCheckpoint]} oldCheckpoint The checkpoint that was used
         * to fetch the current batch of events. This checkpoint will be removed
         * from the index.
         *
         * @return {Promise} A promise that will resolve to true if all the events
         * were already added to the index, false otherwise.
         */

    }, {
        key: "addHistoricEvents",
        value: function () {
            var _ref6 = (0, _bluebird.method)(function (events, checkpoint, oldCheckpoint) {
                throw new Error("Unimplemented");
            });

            function addHistoricEvents(_x4, _x5, _x6) {
                return _ref6.apply(this, arguments);
            }

            return addHistoricEvents;
        }()

        /**
         * Add a new crawler checkpoint to the index.
         *
         * @param {CrawlerCheckpoint} checkpoint The checkpoint that should be added
         * to the index.
         *
         * @return {Promise} A promise that will resolve once the checkpoint has
         * been stored.
         */

    }, {
        key: "addCrawlerCheckpoint",
        value: function () {
            var _ref7 = (0, _bluebird.method)(function (checkpoint) {
                throw new Error("Unimplemented");
            });

            function addCrawlerCheckpoint(_x7) {
                return _ref7.apply(this, arguments);
            }

            return addCrawlerCheckpoint;
        }()

        /**
         * Add a new crawler checkpoint to the index.
         *
         * @param {CrawlerCheckpoint} checkpoint The checkpoint that should be
         * removed from the index.
         *
         * @return {Promise} A promise that will resolve once the checkpoint has
         * been removed.
         */

    }, {
        key: "removeCrawlerCheckpoint",
        value: function () {
            var _ref8 = (0, _bluebird.method)(function (checkpoint) {
                throw new Error("Unimplemented");
            });

            function removeCrawlerCheckpoint(_x8) {
                return _ref8.apply(this, arguments);
            }

            return removeCrawlerCheckpoint;
        }()

        /**
         * Load the stored checkpoints from the index.
         *
         * @return {Promise<[CrawlerCheckpoint]>} A promise that will resolve to an
         * array of crawler checkpoints once they have been loaded from the index.
         */

    }, {
        key: "loadCheckpoints",
        value: function () {
            var _ref9 = (0, _bluebird.method)(function () {
                throw new Error("Unimplemented");
            });

            function loadCheckpoints() {
                return _ref9.apply(this, arguments);
            }

            return loadCheckpoints;
        }()

        /**
         * close our event index.
         *
         * @return {Promise} A promise that will resolve once the event index has
         * been closed.
         */

    }, {
        key: "closeEventIndex",
        value: function () {
            var _ref10 = (0, _bluebird.method)(function () {
                throw new Error("Unimplemented");
            });

            function closeEventIndex() {
                return _ref10.apply(this, arguments);
            }

            return closeEventIndex;
        }()

        /**
         * Delete our current event index.
         *
         * @return {Promise} A promise that will resolve once the event index has
         * been deleted.
         */

    }, {
        key: "deleteEventIndex",
        value: function () {
            var _ref11 = (0, _bluebird.method)(function () {
                throw new Error("Unimplemented");
            });

            function deleteEventIndex() {
                return _ref11.apply(this, arguments);
            }

            return deleteEventIndex;
        }()
    }]);
    return BaseEventIndexManager;
}();

exports.default = BaseEventIndexManager;
module.exports = exports["default"];
//# sourceMappingURL=BaseEventIndexManager.js.map